import reportpurchaseregisterService from '../../../services/reportpurchaseregisterService';
import filterService from '../../../services/filterService';

import {
    setReportPurchaseRegisterLoading,
    setReportPurchaseRegisterError,
    setReportPurchaseRegisterList,
    setReportPurchaseRegisterCurrent,
    setReportPurchaseRegisterPagination,
    setReportPurchaseRegisterSort,
    setReportPurchaseRegisterFilters,
    setReportPurchaseRegisterDefaultFilter,
    setReportPurchaseRegisterSettings,
    clearReportPurchaseRegisterCurrent,
    clearReportPurchaseRegisterError,
    setReportPurchaseRegisterFilterLoading,
    changeReportPurchaseRegisterFilterCondition,
    addReportPurchaseRegisterFilterCondition,
    updateReportPurchaseRegisterFilterConditionRule,
    updateReportPurchaseRegisterFilterConditionValue,
    removeReportPurchaseRegisterFilterCondition,
    setReportPurchaseRegisterSelected,
    setReportPurchaseRegisterDownloading,
    resetReportPurchaseRegisterFiltersAndPagination,

    setReportPurchaseRegisterClassicFilters
} from './reportpurchaseregisterSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().reportpurchaseregister.isSettingsInitialized) {
            dispatch(setReportPurchaseRegisterLoading(true));
            const response = await reportpurchaseregisterService.getSettings();

            // Set the settings
            dispatch(setReportPurchaseRegisterSettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setReportPurchaseRegisterClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setReportPurchaseRegisterDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        dispatch(setReportPurchaseRegisterError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setReportPurchaseRegisterLoading(false));
    }
};

export const fetchReportpurchaseregisters = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().reportpurchaseregister;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setReportPurchaseRegisterLoading(true));
        const response = await reportpurchaseregisterService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setReportPurchaseRegisterList(response));
        return response;
    } catch (error) {

        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportPurchaseRegisterError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportPurchaseRegisterLoading(false));
    }
};

export const fetchReportPurchaseRegister = (id) => async (dispatch) => {
    try {
        dispatch(setReportPurchaseRegisterLoading(true));
        const response = await reportpurchaseregisterService.getOne(id);
        dispatch(setReportPurchaseRegisterCurrent(response.data));
    } catch (error) {
        dispatch(setReportPurchaseRegisterError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setReportPurchaseRegisterLoading(false));
    }
};

export const createReportPurchaseRegister = (data) => async (dispatch) => {
    try {
        const response = await reportpurchaseregisterService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateReportPurchaseRegister = (id, data) => async (dispatch) => {
    try {
        const response = await reportpurchaseregisterService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteReportPurchaseRegister = (id) => async (dispatch) => {
    try {
        dispatch(setReportPurchaseRegisterLoading(true));
        const response = await reportpurchaseregisterService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setReportPurchaseRegisterError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportPurchaseRegisterLoading(false));
    }
};

export const downloadReportPurchaseRegister = (type) => async (dispatch, getState) => {
    try {
        const state = getState().reportpurchaseregister;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setReportPurchaseRegisterDownloading(true));
        const response = await reportpurchaseregisterService.download(finalQueryParams, filter, state.classicFilters);
        dispatch(setReportPurchaseRegisterDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading reportpurchaseregisters:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportPurchaseRegisterError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportPurchaseRegisterDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setReportPurchaseRegisterPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setReportPurchaseRegisterSort(sort));
};

export const searchReportpurchaseregisters = (query) => (dispatch, getState) => {
    const currentFilters = getState().reportpurchaseregister.filters;
    dispatch(setReportPurchaseRegisterFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setReportPurchaseRegisterDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeReportPurchaseRegisterFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addReportPurchaseRegisterFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateReportPurchaseRegisterFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateReportPurchaseRegisterFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeReportPurchaseRegisterFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setReportPurchaseRegisterFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().reportpurchaseregister.settings;
            dispatch(setReportPurchaseRegisterSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setReportPurchaseRegisterFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setReportPurchaseRegisterFilterLoading(false));
        dispatch(setReportPurchaseRegisterError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setReportPurchaseRegisterSelected(selectedRows));
    };
};

export const clearCurrentReportPurchaseRegister = () => {
    return (dispatch) => {
        dispatch(clearReportPurchaseRegisterCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetReportPurchaseRegisterFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().reportpurchaseregister.classicFilters;

    // Create a deep copy of the classic filters
    const updatedClassicFilters = { ...currentClassicFilters };
    
    // Update the nested value in the query object
    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setReportPurchaseRegisterClassicFilters(updatedClassicFilters));
};